import {FC, useEffect, useState, useRef} from "react";

const updateCountdown = (offerExpiresAt: string) => {
    const now = new Date();
    const expiresAt = new Date(offerExpiresAt);
    const timeDiff = expiresAt.getTime() - now.getTime();

    if (timeDiff <= 0) {
        return { text: 'Offer Expired', isExpired: true, isUrgent: true };
    } else {
        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
        const isUrgent = hours < 2; // Less than 2 hours remaining

        return { 
            text: `Offer Expires in ${hours}h ${minutes}m ${seconds}s`,
            isExpired: false,
            isUrgent
        };
    }
};

interface OfferTimerProps {
    expiresAt: string;
    setIsExpired?: (expired: boolean) => void;
}

const OfferTimer: FC<OfferTimerProps> = ({expiresAt, setIsExpired}) => {
    const [time, setTime] = useState<string>('');
    const [isExpired, setIsExpiredState] = useState(false);
    const [isUrgent, setIsUrgent] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const updateTime = () => {
            const result = updateCountdown(expiresAt);
            setTime(result.text);
            setIsExpiredState(result.isExpired);
            setIsUrgent(result.isUrgent);
            setIsExpired?.(result.isExpired);
        };

        // Initial update
        updateTime();
        
        // Set up interval for updates
        intervalRef.current = setInterval(updateTime, 1000);

        // Cleanup interval on unmount
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [expiresAt, setIsExpired]);

    const containerStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: 600,
        backgroundColor: isExpired 
            ? '#FEE2E2' 
            : isUrgent 
                ? '#FEF3C7' 
                : '#DBEAFE',
        color: isExpired 
            ? '#DC2626' 
            : isUrgent 
                ? '#D97706' 
                : '#1D4ED8',
        border: `1px solid ${
            isExpired 
                ? '#FECACA' 
                : isUrgent 
                    ? '#FDE68A' 
                    : '#BFDBFE'
        }`,
    };

    const iconStyle: React.CSSProperties = {
        width: '16px',
        height: '16px',
    };

    return (
        <div style={containerStyle}>
            <svg 
                style={iconStyle} 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                strokeLinecap="round" 
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
            </svg>
            {time}
        </div>
    );
};

export default OfferTimer;
